<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[130] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4"
        @click.self="$emit('close')"
      >
        <div class="w-full max-w-md rounded-xl bg-white shadow-2xl ring-1 ring-zinc-200">
          <div class="relative p-8 text-center">
            <button
              type="button"
              class="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
              @click="$emit('close')"
            >
              <span class="sr-only">Close</span>
              <XMarkIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
            <div
              v-if="isVideoPublishNotification"
              class="mx-auto w-full max-w-[360px]"
            >
              <div
                class="relative h-[180px] w-full overflow-hidden rounded-xl bg-zinc-100 ring-1 ring-zinc-200"
                @mouseenter="activateVideoPreview"
                @mouseleave="deactivateVideoPreview"
              >
                <div
                  v-if="showVideoMediaSkeleton"
                  class="absolute inset-0 notification-media-shimmer"
                />
                <img
                  v-if="activeVideoMediaSrc"
                  :src="activeVideoMediaSrc"
                  alt="Video thumbnail"
                  class="h-full w-full object-cover"
                  :class="{ 'opacity-0': showVideoMediaSkeleton }"
                  @load="onVideoMediaLoad"
                  @error="onVideoMediaError"
                >
              </div>
              <p
                v-if="isFlixVideoPublishNotification"
                class="mt-4 text-lg font-semibold text-zinc-900 break-words"
              >
                {{ flixPublishedVideoTitle }}
              </p>
              <div
                v-else
                class="mt-4 flex items-center justify-center gap-3"
              >
                <img
                  :src="avatarSrc"
                  alt="Author avatar"
                  class="h-10 w-10 rounded-full object-cover ring-1 ring-zinc-200"
                  @error="onAvatarError"
                >
                <p class="text-lg font-semibold text-zinc-900">
                  {{ notification?.title || 'Notification' }}
                </p>
              </div>
            </div>
            <div
              v-else
              class="relative mx-auto inline-flex"
            >
              <img
                :src="avatarSrc"
                alt="Author avatar"
                :class="isBitcoinAvatar
                  ? 'h-[100px] w-[100px] rounded-full object-cover scale-[1.08]'
                  : 'h-[100px] w-[100px] rounded-full object-cover ring-1 ring-zinc-200'"
                @error="onAvatarError"
              >
              <img
                v-if="reactionIcon && !isBitcoinAvatar"
                :src="reactionIcon"
                alt="Reaction"
                class="absolute bottom-1 right-1 h-7 w-7 rounded-full bg-white ring-1 ring-white"
              >
            </div>
            <div class="mt-4 min-w-0">
              <p
                v-if="!isVideoPublishNotification"
                class="text-lg font-semibold"
                :class="notificationTitleClass"
              >
                {{ notification?.title || 'Notification' }}
              </p>
              <p
                v-if="displayBody"
                class="mt-2 text-sm"
                :class="notificationBodyClass"
              >
                {{ displayBody }}
              </p>
              <p
                v-if="createdAtText"
                class="mt-3 text-xs text-zinc-500"
              >
                {{ createdAtText }}
              </p>
              <router-link
                v-if="resolvedActionLinkPath"
                :to="resolvedActionLinkPath"
                class="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2578db] transition-colors"
              >
                {{ actionButtonLabel }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/20/solid';
import defaultAvatar from '@/assets/media/images/default-avatar.png';
import defaultThumbnail from '@/assets/media/images/default-thumbnail.png';
const btcIcon = new URL('@/assets/media/images/btc_icon_Platforma_Icons.svg', import.meta.url).href;
const likeIcon = new URL('@/assets/media/images/newsfeed-like-icon.png', import.meta.url).href;
const loveIcon = new URL('@/assets/media/images/newsfeed-love-icon.png', import.meta.url).href;
const careIcon = new URL('@/assets/media/images/newsfeed-care-icon.png', import.meta.url).href;
const hahaIcon = new URL('@/assets/media/images/newsfeed-haha-icon.png', import.meta.url).href;
const wowIcon = new URL('@/assets/media/images/newsfeed-wow-icon.png', import.meta.url).href;
const sadIcon = new URL('@/assets/media/images/newsfeed-sad-icon.png', import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry-icon.png', import.meta.url).href;
const commentIcon = new URL('@/assets/media/images/comment-platforma-newsfeed-icon.png', import.meta.url).href;
const postIcon = new URL('@/assets/media/images/post-platforma-newsfeed-icon.png', import.meta.url).href;

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  notification: {
    type: Object,
    default: null,
  },
});

defineEmits(['close']);

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';
const isVideoPreviewHover = ref(false);
const videoPreviewFailed = ref(false);
const videoThumbnailFailed = ref(false);
const videoThumbnailCandidateIndex = ref(0);
const isVideoMediaLoading = ref(true);
const fallbackHydrationComplete = ref(false);
const fallbackVideoMeta = ref({
  mediaFileId: '',
  thumbnailMediaFileId: '',
  thumbnailUrl: '',
});
let fallbackLoadToken = 0;

function parsePayload(notification) {
  const raw = notification?.payload;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeAvatarSrc(value, featureInstanceId) {
  const raw = String(value || '').trim();
  if (!raw) return defaultAvatar;
  const match = raw.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (!match?.[1]) return raw;

  const params = new URLSearchParams();
  const iid = Number(featureInstanceId || 0);
  if (Number.isFinite(iid) && iid > 0) {
    params.set('featureInstanceId', String(iid));
  }
  const qs = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${match[1]}${qs ? `?${qs}` : ''}`;
}

function onAvatarError(event) {
  const img = event?.target;
  if (!img || img.tagName !== 'IMG') return;
  img.src = defaultAvatar;
}

function resolveVideoPublishEventType(notification, payload = {}) {
  const eventType = String(
    notification?.eventType || payload?.eventType || payload?.source || ''
  ).toLowerCase().trim();
  if (eventType === 'video_channel_video_published') return 'video-channel';
  if (eventType === 'flix_theatre_video_published') return 'flix-theatre';
  return '';
}

function isVideoPublishNotificationEvent(notification, payload = {}) {
  return !!resolveVideoPublishEventType(notification, payload);
}
function resolveStorefrontNotificationEventType(notification, payload = {}) {
  const eventType = String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.source ||
      ''
  ).toLowerCase().trim();
  if (eventType.startsWith('storefront_')) return eventType;
  return '';
}
function resolveNotificationEventType(notification, payload = {}) {
  return String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.source ||
      ''
  ).toLowerCase().trim();
}
function isStorefrontNotificationEvent(notification, payload = {}) {
  return !!resolveStorefrontNotificationEventType(notification, payload);
}
function isCryptoReceiveNotificationEvent(notification, payload = {}) {
  return resolveNotificationEventType(notification, payload) === 'crypto_btc_received';
}
function isCreatorMemberSubscriptionPaymentFailedNotificationEvent(notification, payload = {}) {
  const eventType = resolveNotificationEventType(notification, payload);
  return (
    eventType === 'member_subscription_payment_failed' ||
    eventType === 'member.subscription_payment_failed' ||
    eventType === 'subscription_payment_failed'
  );
}
function isCreatorFeaturePaywallUnlockedNotificationEvent(notification, payload = {}) {
  const eventType = resolveNotificationEventType(notification, payload);
  return (
    eventType === 'member_feature_paywall_unlocked' ||
    eventType === 'member.feature_paywall_unlocked' ||
    eventType === 'feature_paywall_unlocked'
  );
}
function resolveStorefrontMemberAvatar(notification, payload = {}) {
  const raw = String(
    payload?.memberAvatarUrl ||
      notification?.memberAvatarUrl ||
      payload?.member_avatar_url ||
      notification?.member_avatar_url ||
      payload?.avatarUrl ||
      notification?.avatarUrl ||
      payload?.avatar_url ||
      notification?.avatar_url ||
      ''
  ).trim();
  if (!raw) return '';
  return normalizeAvatarSrc(
    raw,
    notification?.featureInstanceId || notification?.feature_instance_id
  );
}
function buildStorefrontModalBody(notification, payload = {}) {
  const storefrontName = String(
    payload?.storefrontName ||
      payload?.storefront_name ||
      payload?.storeName ||
      payload?.store_name ||
      payload?.featureInstanceName ||
      payload?.instanceName ||
      payload?.storefrontTitle ||
      'Storefront'
  ).trim() || 'Storefront';
  const orderNumber = String(
    payload?.orderNumber || payload?.order_number || notification?.orderNumber || ''
  ).trim();
  if (orderNumber) return `place order #${orderNumber} in ${storefrontName}`;
  return `place order in ${storefrontName}`;
}
function buildCreatorStorefrontOrdersLinkPath(notification, payload = {}) {
  const featureSlug = String(
    payload?.featureSlug || payload?.feature_slug || ''
  ).trim();
  const featureInstanceId = Number(
    payload?.featureInstanceId ||
      payload?.feature_instance_id ||
      notification?.featureInstanceId ||
      notification?.feature_instance_id ||
      0
  );
  if (!featureSlug || !Number.isFinite(featureInstanceId) || featureInstanceId <= 0) return '';
  return `/creator/theme-editor/${encodeURIComponent(featureSlug)}/stockroom/orders?instanceId=${Math.trunc(featureInstanceId)}`;
}
function convertLegacyCreatorStorefrontOrdersPath(pathValue, notification, payload = {}) {
  const raw = String(pathValue || '').trim();
  if (!raw) return '';
  const featureInstanceId = Number(
    payload?.featureInstanceId ||
      payload?.feature_instance_id ||
      notification?.featureInstanceId ||
      notification?.feature_instance_id ||
      0
  );
  if (raw.includes('/creator/theme-editor/') && raw.includes('/stockroom/orders')) return raw;
  const legacyMatch = raw.match(/^\/creator\/([^/]+)\/stockroom\/orders\/\d+$/i);
  if (legacyMatch?.[1]) {
    const safeSlug = decodeURIComponent(String(legacyMatch[1] || '').trim());
    if (safeSlug && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
      return `/creator/theme-editor/${encodeURIComponent(safeSlug)}/stockroom/orders?instanceId=${Math.trunc(featureInstanceId)}`;
    }
  }
  return raw.includes('/stockroom/orders') ? raw : '';
}

function resolveFeatureInstanceId(notification, payload = {}) {
  const iid = Number(
    notification?.featureInstanceId || payload?.featureInstanceId || payload?.instanceId || 0
  );
  return Number.isFinite(iid) && iid > 0 ? Math.trunc(iid) : 0;
}

function isBtcAvatarSrc(value) {
  const src = String(value || '').trim();
  return src.includes('btc_icon_Platforma_Icons.svg');
}

function resolveCreatorVideoPublishAvatar(notification, payload = {}) {
  const eventType = resolveVideoPublishEventType(notification, payload);
  if (!eventType) return '';
  const featureInstanceId = Number(
    notification?.featureInstanceId || payload?.featureInstanceId || payload?.instanceId || 0
  );
  const fileId = String(
    payload?.channelAvatarMediaFileId || payload?.theatreAvatarMediaFileId || ''
  ).trim();
  if (fileId && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(Math.trunc(featureInstanceId)),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    return `${apiProxyUrl}${imagePrefix}${encodeURIComponent(fileId)}?${qs}`;
  }

  const direct = String(
    payload?.creatorAvatarUrl ||
      payload?.theatreAvatarUrl ||
      payload?.channelAvatarUrl ||
      payload?.authorAvatar ||
      payload?.authorAvatarUrl ||
      payload?.avatarUrl ||
      ''
  ).trim();
  const match = direct.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (match?.[1] && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(Math.trunc(featureInstanceId)),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    return `${apiProxyUrl}${imagePrefix}${encodeURIComponent(match[1])}?${qs}`;
  }
  return direct || '';
}

const avatarSrc = computed(() => {
  const payload = parsePayload(props.notification);
  if (isCryptoReceiveNotificationEvent(props.notification, payload)) {
    return btcIcon;
  }
  if (isStorefrontNotificationEvent(props.notification, payload)) {
    const storefrontMemberAvatar = resolveStorefrontMemberAvatar(props.notification, payload);
    if (storefrontMemberAvatar) return storefrontMemberAvatar;
  }
  if (isVideoPublishNotificationEvent(props.notification, payload)) {
    const direct = resolveCreatorVideoPublishAvatar(props.notification, payload);
    if (direct) return direct;
  }
  const raw = String(
    payload?.authorAvatar ||
      payload?.authorAvatarUrl ||
      payload?.avatarUrl ||
      ''
  ).trim();
  return normalizeAvatarSrc(raw, props.notification?.featureInstanceId);
});
const isCryptoReceiveNotification = computed(() => {
  const payload = parsePayload(props.notification);
  return isCryptoReceiveNotificationEvent(props.notification, payload);
});
const isMemberSubscriptionPaymentFailedNotification = computed(() => {
  const payload = parsePayload(props.notification);
  return isCreatorMemberSubscriptionPaymentFailedNotificationEvent(props.notification, payload);
});
const notificationTitleClass = computed(() =>
  isMemberSubscriptionPaymentFailedNotification.value ? 'text-red-600' : 'text-zinc-900'
);
const notificationBodyClass = computed(() =>
  isMemberSubscriptionPaymentFailedNotification.value ? 'text-red-500' : 'text-zinc-600'
);
const isBitcoinAvatar = computed(
  () => isCryptoReceiveNotification.value || isBtcAvatarSrc(avatarSrc.value)
);

const isVideoPublishNotification = computed(() => {
  const payload = parsePayload(props.notification);
  return isVideoPublishNotificationEvent(props.notification, payload);
});
const videoPublishEventType = computed(() => {
  const payload = parsePayload(props.notification);
  return resolveVideoPublishEventType(props.notification, payload);
});
const isFlixVideoPublishNotification = computed(() => videoPublishEventType.value === 'flix-theatre');
const flixFeatureTitle = computed(() => {
  const payload = parsePayload(props.notification);
  return String(
    payload?.theatreTitle || props.notification?.title || 'Flix Theatre'
  ).trim() || 'Flix Theatre';
});
const flixPublishedVideoTitle = computed(() => {
  const payload = parsePayload(props.notification);
  return String(
    payload?.videoTitle || payload?.title || payload?.video_slug || 'Video'
  ).trim() || 'Video';
});
const displayBody = computed(() => {
  const payload = parsePayload(props.notification);
  if (isStorefrontNotificationEvent(props.notification, payload)) {
    return buildStorefrontModalBody(props.notification, payload);
  }
  if (isFlixVideoPublishNotification.value) {
    return `New Streaming Release in ${flixFeatureTitle.value}`;
  }
  return String(props.notification?.body || '').trim();
});

function buildCreatorVideoThumbnailCandidates(notification, payload = {}) {
  const eventType = resolveVideoPublishEventType(notification, payload);
  if (!eventType) return [];
  const featureInstanceId = resolveFeatureInstanceId(notification, payload);
  const thumbnailMediaFileId = String(
    payload?.videoThumbnailMediaFileId ||
      payload?.videoPlaylistThumbnailMediaFileId ||
      payload?.videoFeatureThumbnailMediaFileId ||
      fallbackVideoMeta.value.thumbnailMediaFileId ||
      ''
  ).trim();
  const mediaFileId = String(
    payload?.videoMediaFileId || fallbackVideoMeta.value.mediaFileId || ''
  ).trim();
  const direct = String(
    payload?.videoThumbnailUrl ||
      payload?.videoPlaylistThumbnailUrl ||
      payload?.videoFeatureThumbnailUrl ||
      payload?.thumbnailUrl ||
      fallbackVideoMeta.value.thumbnailUrl ||
      ''
  ).trim();
  const candidates = [];

  if (thumbnailMediaFileId && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(featureInstanceId),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    candidates.push(
      `${apiProxyUrl}${imagePrefix}${encodeURIComponent(thumbnailMediaFileId)}?${qs}`
    );
  }
  if (mediaFileId && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(featureInstanceId),
    }).toString();
    const generatedPrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/video-thumbnail/'
        : '/api/creator-video-channel/video-thumbnail/';
    candidates.push(
      `${apiProxyUrl}${generatedPrefix}${encodeURIComponent(mediaFileId)}?${qs}`
    );
  }
  const directImageMatch = direct.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (directImageMatch?.[1] && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(featureInstanceId),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    candidates.push(
      `${apiProxyUrl}${imagePrefix}${encodeURIComponent(directImageMatch[1])}?${qs}`
    );
  } else if (direct) {
    candidates.push(direct);
  }
  return Array.from(new Set(candidates.filter(Boolean)));
}

function resolveCreatorVideoHoverPreviewSrc(notification, payload = {}) {
  const eventType = resolveVideoPublishEventType(notification, payload);
  if (!eventType) return '';
  const featureInstanceId = resolveFeatureInstanceId(notification, payload);
  const mediaFileId = String(
    payload?.videoMediaFileId || fallbackVideoMeta.value.mediaFileId || ''
  ).trim();
  if (!mediaFileId || featureInstanceId <= 0) return '';
  const qs = new URLSearchParams({
    featureInstanceId: String(featureInstanceId),
  }).toString();
  const hoverPrefix =
    eventType === 'flix-theatre'
      ? '/api/creator-flix-theatre/hover-preview/'
      : '/api/creator-video-channel/hover-preview/';
  return `${apiProxyUrl}${hoverPrefix}${encodeURIComponent(mediaFileId)}?${qs}`;
}

function parseCreatorWatchLink(linkPath) {
  const raw = String(linkPath || '').trim();
  if (!raw) return { featureInstanceId: 0, videoSlug: '' };
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(raw, origin);
    const match = u.pathname.match(/\/watch\/([^/?#]+)/i);
    const videoSlug = decodeURIComponent(String(match?.[1] || '').trim());
    const featureInstanceId = Number(u.searchParams.get('instanceId') || 0);
    return {
      featureInstanceId: Number.isFinite(featureInstanceId) ? Math.trunc(featureInstanceId) : 0,
      videoSlug,
    };
  } catch {
    return { featureInstanceId: 0, videoSlug: '' };
  }
}

function buildCreatorFlixInfoLinkPath({ featureSlug, videoSlug, featureInstanceId }) {
  const safeFeatureSlug = String(featureSlug || '').trim();
  const safeVideoSlug = String(videoSlug || '').trim();
  const safeFeatureInstanceId = Number(featureInstanceId || 0);
  if (!safeFeatureSlug || !safeVideoSlug || safeFeatureInstanceId <= 0) return '';
  const qs = new URLSearchParams({
    instanceId: String(Math.trunc(safeFeatureInstanceId)),
    openInfo: '1',
    videoSlug: safeVideoSlug,
  }).toString();
  return `/creator/theme-editor/${encodeURIComponent(safeFeatureSlug)}?${qs}`;
}

async function hydrateFallbackVideoMeta() {
  const myToken = ++fallbackLoadToken;
  fallbackHydrationComplete.value = false;
  const payload = parsePayload(props.notification);
  const eventType = resolveVideoPublishEventType(props.notification, payload);
  if (!eventType) {
    if (myToken === fallbackLoadToken) fallbackHydrationComplete.value = true;
    return;
  }

  const hasPayloadMediaFileId = String(payload?.videoMediaFileId || '').trim();
  if (hasPayloadMediaFileId) {
    if (myToken === fallbackLoadToken) fallbackHydrationComplete.value = true;
    return;
  }

  const featureInstanceId = resolveFeatureInstanceId(props.notification, payload);
  const parsed = parseCreatorWatchLink(
    props.notification?.linkPath || payload?.creatorLinkPath || ''
  );
  const videoSlug = String(parsed.videoSlug || payload?.videoSlug || '').trim();
  const instanceId = parsed.featureInstanceId || featureInstanceId;
  if (!videoSlug || !instanceId) {
    if (myToken === fallbackLoadToken) fallbackHydrationComplete.value = true;
    return;
  }

  try {
    const qs = new URLSearchParams({
      featureInstanceId: String(instanceId),
    }).toString();
    const watchPrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/watch/'
        : '/api/creator-video-channel/watch/';
    const response = await fetch(
      `${apiProxyUrl}${watchPrefix}${encodeURIComponent(videoSlug)}?${qs}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) return;
    const data = await response.json().catch(() => ({}));
    const video = data?.video || data?.activeVideo || {};
    if (myToken !== fallbackLoadToken) return;
    fallbackVideoMeta.value = {
      mediaFileId: String(video?.mediaFileId || '').trim(),
      thumbnailMediaFileId: String(video?.thumbnailMediaFileId || '').trim(),
      thumbnailUrl: String(video?.thumbnailUrl || '').trim(),
    };
  } catch (_error) {
    // no-op: graceful fallback to default thumbnail
  } finally {
    if (myToken === fallbackLoadToken) {
      fallbackHydrationComplete.value = true;
    }
  }
}

const videoThumbnailCandidates = computed(() => {
  const payload = parsePayload(props.notification);
  if (!isVideoPublishNotificationEvent(props.notification, payload)) return [];
  return buildCreatorVideoThumbnailCandidates(props.notification, payload);
});

const videoThumbnailSrc = computed(() => {
  if (videoThumbnailFailed.value) return defaultThumbnail;
  const candidates = videoThumbnailCandidates.value;
  if (!candidates.length) {
    return fallbackHydrationComplete.value ? defaultThumbnail : '';
  }
  const index = Math.max(0, Math.min(videoThumbnailCandidateIndex.value, candidates.length - 1));
  return candidates[index] || defaultThumbnail;
});

const videoHoverPreviewSrc = computed(() => {
  if (videoPreviewFailed.value) return '';
  const payload = parsePayload(props.notification);
  if (!isVideoPublishNotificationEvent(props.notification, payload)) return '';
  return resolveCreatorVideoHoverPreviewSrc(props.notification, payload);
});

const activeVideoMediaSrc = computed(() => {
  if (isVideoPreviewHover.value && videoHoverPreviewSrc.value) return videoHoverPreviewSrc.value;
  return videoThumbnailSrc.value || defaultThumbnail;
});

const resolvedActionLinkPath = computed(() => {
  const payload = parsePayload(props.notification);
  const fallbackLinkPath = String(props.notification?.linkPath || '').trim();
  if (isStorefrontNotificationEvent(props.notification, payload)) {
    const stockroomOrdersPath = buildCreatorStorefrontOrdersLinkPath(props.notification, payload);
    const creatorPayloadPath = String(payload?.creatorLinkPath || '').trim();
    const fallbackCreatorPath = String(fallbackLinkPath || '').trim();
    if (stockroomOrdersPath) return stockroomOrdersPath;
    const normalizedCreatorPayloadPath = convertLegacyCreatorStorefrontOrdersPath(
      creatorPayloadPath,
      props.notification,
      payload
    );
    if (normalizedCreatorPayloadPath) return normalizedCreatorPayloadPath;
    const normalizedFallbackPath = convertLegacyCreatorStorefrontOrdersPath(
      fallbackCreatorPath,
      props.notification,
      payload
    );
    if (normalizedFallbackPath) return normalizedFallbackPath;
    return '';
  }
  const eventType = resolveVideoPublishEventType(props.notification, payload);
  if (eventType !== 'flix-theatre') return fallbackLinkPath;

  const parsed = parseCreatorWatchLink(
    fallbackLinkPath || String(payload?.creatorLinkPath || '').trim()
  );
  const featureSlug = String(payload?.featureSlug || '').trim();
  const videoSlug = String(payload?.videoSlug || parsed.videoSlug || '').trim();
  const featureInstanceId = Number(
    resolveFeatureInstanceId(props.notification, payload) || parsed.featureInstanceId || 0
  );

  const flixInfoPath = buildCreatorFlixInfoLinkPath({
    featureSlug,
    videoSlug,
    featureInstanceId,
  });
  return flixInfoPath || fallbackLinkPath;
});

const showVideoMediaSkeleton = computed(() => {
  if (!isVideoPublishNotification.value) return false;
  if (isVideoPreviewHover.value) return false;
  return isVideoMediaLoading.value || !videoThumbnailSrc.value;
});

function activateVideoPreview() {
  if (!videoHoverPreviewSrc.value) return;
  isVideoPreviewHover.value = true;
}

function deactivateVideoPreview() {
  isVideoPreviewHover.value = false;
}

function onVideoMediaLoad() {
  if (!isVideoPreviewHover.value) {
    isVideoMediaLoading.value = false;
  }
}

function onVideoMediaError() {
  if (isVideoPreviewHover.value && videoHoverPreviewSrc.value) {
    videoPreviewFailed.value = true;
    isVideoPreviewHover.value = false;
    return;
  }
  const candidates = videoThumbnailCandidates.value;
  if (videoThumbnailCandidateIndex.value < Math.max(candidates.length - 1, 0)) {
    videoThumbnailCandidateIndex.value += 1;
    isVideoMediaLoading.value = true;
    return;
  }
  videoThumbnailFailed.value = true;
  isVideoMediaLoading.value = false;
}

const actionButtonLabel = computed(() => {
  const payload = parsePayload(props.notification);
  const explicitLabel = String(
    props.notification?.linkLabel || payload?.linkLabel || payload?.link_label || ''
  ).trim();
  if (explicitLabel) return explicitLabel;
  if (isCryptoReceiveNotificationEvent(props.notification, payload)) {
    return 'See transaction';
  }
  if (isStorefrontNotificationEvent(props.notification, payload)) {
    return 'View Order';
  }
  if (isCreatorFeaturePaywallUnlockedNotificationEvent(props.notification, payload)) {
    return 'See Details';
  }
  if (isCreatorMemberSubscriptionPaymentFailedNotificationEvent(props.notification, payload)) {
    return 'See Details';
  }
  if (!isVideoPublishNotificationEvent(props.notification, payload)) {
    return 'Check Out Post';
  }
  const eventType = resolveVideoPublishEventType(props.notification, payload);
  return eventType === 'flix-theatre' ? 'Check It Out Now' : 'Watch Video Now';
});

watch(
  () => props.notification?.notificationSlug,
  () => {
    isVideoPreviewHover.value = false;
    videoPreviewFailed.value = false;
    videoThumbnailFailed.value = false;
    videoThumbnailCandidateIndex.value = 0;
    isVideoMediaLoading.value = true;
    fallbackHydrationComplete.value = false;
    fallbackVideoMeta.value = {
      mediaFileId: '',
      thumbnailMediaFileId: '',
      thumbnailUrl: '',
    };
    void hydrateFallbackVideoMeta();
  }
);

watch(
  () => videoThumbnailCandidates.value.join('|'),
  () => {
    videoThumbnailCandidateIndex.value = 0;
    videoThumbnailFailed.value = false;
    isVideoMediaLoading.value = true;
  }
);

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return;
    isVideoMediaLoading.value = true;
    void hydrateFallbackVideoMeta();
  }
);

watch(
  () => videoThumbnailSrc.value,
  (src) => {
    if (!src) {
      isVideoMediaLoading.value = true;
    }
  }
);

const createdAtText = computed(() => {
  const value = props.notification?.createdAt;
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
});

const reactionIcon = computed(() => {
  const payload = parsePayload(props.notification);
  const eventType = String(
    props.notification?.eventType || payload?.eventType || payload?.source || ''
  ).toLowerCase().trim();
  const type = String(payload?.reactionType || '').toLowerCase().trim();
  if (type === 'like') return likeIcon;
  if (type === 'love') return loveIcon;
  if (type === 'care') return careIcon;
  if (type === 'haha') return hahaIcon;
  if (type === 'wow') return wowIcon;
  if (type === 'sad') return sadIcon;
  if (type === 'angry') return angryIcon;
  if (
    eventType === 'newsfeed_post_created'
  ) {
    return postIcon;
  }
  if (
    eventType === 'newsfeed_comment_created' ||
    eventType === 'newsfeed_comment_on_your_post' ||
    eventType === 'newsfeed_reply_created' ||
    eventType === 'newsfeed_reply_on_your_comment'
  ) {
    return commentIcon;
  }
  return '';
});
</script>

<style scoped>
.notification-media-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(228, 231, 236, 0.75) 0%,
    rgba(241, 244, 249, 0.95) 38%,
    rgba(228, 231, 236, 0.75) 70%
  );
  background-size: 220% 100%;
  animation: notificationShimmer 1.15s linear infinite;
}

@keyframes notificationShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
